/**
 * EditorUI - Main UI controller for the Map Editor
 * Creates and manages all DOM-based UI components
 */

import { HistoryManager, Actions } from './HistoryManager.js';

// HTML escape helper to prevent XSS
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export class EditorUI {
  constructor(scene) {
    this.scene = scene;
    this.history = new HistoryManager(100);

    // State
    this.currentTool = 'paint';
    this.currentLayer = 'background';
    this.selectedTile = null;
    this.selectedTileIndex = 0;
    this.selectedEntity = null;
    this.tiles = [];
    this.categories = [];
    this.maps = [];
    this.layerVisibility = {
      background: true,
      collision: true,
      decoration: true,
      entities: true,
    };

    // Map data
    this.mapData = null;
    this.mapId = null;
    this.hasUnsavedChanges = false;
    this.autoSaveTimer = null;

    // DOM elements
    this.container = null;

    // Bind methods
    this.handleKeydown = this.handleKeydown.bind(this);

    // Create UI
    this.createUI();
    this.loadTiles();
    this.loadMaps();
    this.setupKeyboardShortcuts();
    this.startAutoSave();

    // Listen to history changes
    this.history.addListener((state) => this.updateUndoRedoButtons(state));
  }

  createUI() {
    // Create main container
    this.container = document.createElement('div');
    this.container.id = 'editor-ui';
    document.body.appendChild(this.container);

    // Import CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/src/editor/styles/editor.css';
    document.head.appendChild(link);

    this.container.innerHTML = `
      <!-- Top Toolbar -->
      <div id="editor-toolbar" class="editor-panel">
        <div class="logo">DEAD SECTOR // MAP EDITOR</div>

        <div class="toolbar-group" id="level-picker">
          <button class="editor-btn" id="btn-new-map">NEW</button>
          <button class="editor-btn" id="btn-load-map">LOAD</button>
          <button class="editor-btn" id="btn-save-map">SAVE</button>
          <button class="editor-btn" id="btn-metadata">META</button>
        </div>

        <div class="toolbar-group">
          <button class="editor-btn tool-btn active" data-tool="paint" title="Paint [B]">P</button>
          <button class="editor-btn tool-btn" data-tool="erase" title="Erase [E]">X</button>
          <button class="editor-btn tool-btn" data-tool="select" title="Select [S]">[]</button>
          <button class="editor-btn tool-btn" data-tool="fill" title="Fill [F]">~</button>
        </div>

        <div class="toolbar-group">
          <button class="editor-btn editor-btn-icon" id="btn-undo" title="Undo [Ctrl+Z]" disabled>&lt;</button>
          <button class="editor-btn editor-btn-icon" id="btn-redo" title="Redo [Ctrl+Y]" disabled>&gt;</button>
        </div>

        <div class="toolbar-group">
          <button class="editor-btn" id="btn-grid" title="Toggle Grid [G]">GRID</button>
          <button class="editor-btn" id="btn-test" title="Test Map">TEST</button>
        </div>
      </div>

      <!-- Left Sidebar - Tile Selector -->
      <div id="tile-selector" class="editor-panel">
        <h3>TILES</h3>
        <div id="tile-categories"></div>
      </div>

      <!-- Right Sidebar -->
      <div id="right-sidebar">
        <!-- Entity Palette -->
        <div id="entity-palette" class="editor-panel">
          <h3>ENTITIES</h3>

          <div class="entity-category">
            <div class="entity-category-header">ENEMIES</div>
            <div class="entity-item" data-entity="zombie_shambler">
              <span class="entity-icon">Z</span>
              <span>Zombie (Shambler)</span>
            </div>
            <div class="entity-item" data-entity="zombie_crawler">
              <span class="entity-icon">C</span>
              <span>Zombie (Crawler)</span>
            </div>
            <div class="entity-item" data-entity="boss">
              <span class="entity-icon">B</span>
              <span>Boss</span>
            </div>
          </div>

          <div class="entity-category">
            <div class="entity-category-header">FRIENDLIES</div>
            <div class="entity-item" data-entity="scientist">
              <span class="entity-icon">S</span>
              <span>Scientist</span>
            </div>
            <div class="entity-item" data-entity="survivor">
              <span class="entity-icon">H</span>
              <span>Survivor</span>
            </div>
          </div>

          <div class="entity-category">
            <div class="entity-category-header">SPAWN POINTS</div>
            <div class="entity-item" data-entity="player_spawn">
              <span class="entity-icon">P</span>
              <span>Player Start</span>
            </div>
            <div class="entity-item" data-entity="enemy_spawn">
              <span class="entity-icon">E</span>
              <span>Enemy Spawn Zone</span>
            </div>
          </div>
        </div>

        <!-- Layer Controls -->
        <div id="layer-controls" class="editor-panel">
          <h3>LAYERS</h3>
          <div class="layer-item active" data-layer="background">
            <span class="layer-visibility" data-layer="background">O</span>
            <span class="layer-color" style="background: #39ff14"></span>
            <span>Background</span>
          </div>
          <div class="layer-item" data-layer="collision">
            <span class="layer-visibility" data-layer="collision">O</span>
            <span class="layer-color" style="background: #ff4444"></span>
            <span>Collision</span>
          </div>
          <div class="layer-item" data-layer="decoration">
            <span class="layer-visibility" data-layer="decoration">O</span>
            <span class="layer-color" style="background: #00ffff"></span>
            <span>Decoration</span>
          </div>
          <div class="layer-item" data-layer="entities">
            <span class="layer-visibility" data-layer="entities">O</span>
            <span class="layer-color" style="background: #ff00ff"></span>
            <span>Entities</span>
          </div>
        </div>
      </div>

      <!-- Bottom Status Bar -->
      <div id="editor-status" class="editor-panel">
        <div class="status-group">
          <div class="status-item">
            <span class="status-label">MAP:</span>
            <span class="status-value" id="status-map-name">Untitled</span>
          </div>
          <div class="status-item">
            <span class="status-label">SIZE:</span>
            <span class="status-value" id="status-map-size">25x19</span>
          </div>
          <div class="status-item">
            <span class="status-label">CURSOR:</span>
            <span class="status-value" id="status-cursor">0, 0</span>
          </div>
        </div>
        <div class="status-group">
          <div class="status-item">
            <span class="status-label">TOOL:</span>
            <span class="status-value" id="status-tool">PAINT</span>
          </div>
          <div class="status-item">
            <span class="status-label">LAYER:</span>
            <span class="status-value" id="status-layer">BACKGROUND</span>
          </div>
          <div class="status-item" id="autosave-status" style="display: none;">
            <span class="autosave-indicator">AUTO-SAVING...</span>
          </div>
        </div>
      </div>

      <!-- Metadata Panel (hidden by default) -->
      <div id="metadata-panel" class="editor-panel">
        <h3>MAP SETTINGS</h3>
        <label class="editor-label">Map Name</label>
        <input type="text" class="editor-input" id="meta-name" placeholder="Enter map name">

        <label class="editor-label">Music Track</label>
        <select class="editor-select" id="meta-music">
          <option value="">None</option>
        </select>

        <label class="editor-label">Mission Objectives</label>
        <textarea class="editor-input" id="meta-objectives" rows="3" placeholder="One objective per line"></textarea>

        <button class="editor-btn" id="btn-meta-apply">APPLY</button>
      </div>

      <!-- Shortcuts Help -->
      <div id="shortcuts-help">
        <kbd>B</kbd> Paint | <kbd>E</kbd> Erase | <kbd>S</kbd> Select | <kbd>F</kbd> Fill<br>
        <kbd>1-4</kbd> Layers | <kbd>G</kbd> Grid | <kbd>Ctrl+S</kbd> Save<br>
        <kbd>Ctrl+Z</kbd> Undo | <kbd>Ctrl+Y</kbd> Redo
      </div>
    `;

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Tool buttons
    document.querySelectorAll('.tool-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this.setTool(btn.dataset.tool);
      });
    });

    // Layer items (click for active, visibility toggle)
    document.querySelectorAll('.layer-item').forEach(item => {
      item.addEventListener('click', (e) => {
        if (e.target.classList.contains('layer-visibility')) {
          this.toggleLayerVisibility(item.dataset.layer);
        } else {
          this.setActiveLayer(item.dataset.layer);
        }
      });
    });

    // Entity items
    document.querySelectorAll('.entity-item').forEach(item => {
      item.addEventListener('click', () => {
        this.selectEntity(item.dataset.entity);
      });
    });

    // Toolbar buttons
    document.getElementById('btn-new-map').addEventListener('click', () => this.showNewMapDialog());
    document.getElementById('btn-load-map').addEventListener('click', () => this.showLoadMapDialog());
    document.getElementById('btn-save-map').addEventListener('click', () => this.saveMap());
    document.getElementById('btn-metadata').addEventListener('click', () => this.toggleMetadataPanel());
    document.getElementById('btn-undo').addEventListener('click', () => this.history.undo());
    document.getElementById('btn-redo').addEventListener('click', () => this.history.redo());
    document.getElementById('btn-grid').addEventListener('click', () => this.scene.toggleGrid());
    document.getElementById('btn-test').addEventListener('click', () => this.testMap());
    document.getElementById('btn-meta-apply').addEventListener('click', () => this.applyMetadata());
  }

  setupKeyboardShortcuts() {
    document.addEventListener('keydown', this.handleKeydown);
  }

  handleKeydown(e) {
    // Ignore if typing in input
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

    // Tool shortcuts
    if (e.key.toLowerCase() === 'b') this.setTool('paint');
    if (e.key.toLowerCase() === 'e') this.setTool('erase');
    if (e.key.toLowerCase() === 's' && !e.ctrlKey) this.setTool('select');
    if (e.key.toLowerCase() === 'f') this.setTool('fill');

    // Layer shortcuts
    if (e.key === '1') this.setActiveLayer('background');
    if (e.key === '2') this.setActiveLayer('collision');
    if (e.key === '3') this.setActiveLayer('decoration');
    if (e.key === '4') this.setActiveLayer('entities');

    // Grid toggle
    if (e.key.toLowerCase() === 'g') this.scene.toggleGrid();

    // Undo/Redo
    if (e.ctrlKey && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      this.history.undo();
    }
    if (e.ctrlKey && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      this.history.redo();
    }

    // Save
    if (e.ctrlKey && e.key.toLowerCase() === 's') {
      e.preventDefault();
      this.saveMap();
    }
  }

  async loadTiles() {
    try {
      const response = await fetch('/api/editor/tiles');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.tiles = data.tiles;
      this.categories = data.categories;
      this.renderTileSelector();
    } catch (error) {
      console.error('Failed to load tiles:', error);
    }
  }

  renderTileSelector() {
    const container = document.getElementById('tile-categories');
    container.innerHTML = '';

    // Group tiles by category while preserving global index
    const grouped = {};
    this.tiles.forEach((tile, globalIndex) => {
      if (!grouped[tile.category]) {
        grouped[tile.category] = [];
      }
      grouped[tile.category].push({ tile, globalIndex });
    });

    // Render each category
    Object.entries(grouped).forEach(([category, items]) => {
      const categoryDiv = document.createElement('div');
      categoryDiv.className = 'tile-category';
      categoryDiv.innerHTML = `
        <div class="tile-category-header">${escapeHtml(category)}</div>
        <div class="tile-grid"></div>
      `;

      const grid = categoryDiv.querySelector('.tile-grid');
      items.forEach(({ tile, globalIndex }) => {
        const tileItem = document.createElement('div');
        tileItem.className = 'tile-item';
        tileItem.dataset.tilePath = tile.path;
        tileItem.dataset.tileIndex = globalIndex;
        tileItem.innerHTML = `<img src="${escapeHtml(tile.path)}" alt="${escapeHtml(tile.filename)}">`;
        tileItem.addEventListener('click', () => this.selectTile(tile, globalIndex));
        grid.appendChild(tileItem);
      });

      container.appendChild(categoryDiv);
    });
  }

  selectTile(tile, index) {
    // Clear entity selection
    this.selectedEntity = null;
    document.querySelectorAll('.entity-item').forEach(e => e.classList.remove('selected'));

    // Update tile selection
    this.selectedTile = tile;
    this.selectedTileIndex = index;

    document.querySelectorAll('.tile-item').forEach(t => t.classList.remove('selected'));
    document.querySelector(`[data-tile-index="${index}"]`)?.classList.add('selected');

    // Notify scene
    this.scene.onTileSelected(tile, index);
  }

  selectEntity(entityType) {
    // Clear tile selection
    this.selectedTile = null;
    document.querySelectorAll('.tile-item').forEach(t => t.classList.remove('selected'));

    // Update entity selection
    this.selectedEntity = entityType;

    document.querySelectorAll('.entity-item').forEach(e => e.classList.remove('selected'));
    document.querySelector(`[data-entity="${entityType}"]`)?.classList.add('selected');

    // Set to entities layer
    this.setActiveLayer('entities');

    // Notify scene
    this.scene.onEntitySelected(entityType);
  }

  setTool(tool) {
    this.currentTool = tool;

    document.querySelectorAll('.tool-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tool === tool);
    });

    document.getElementById('status-tool').textContent = tool.toUpperCase();

    // Notify scene
    this.scene.onToolChanged(tool);
  }

  setActiveLayer(layer) {
    this.currentLayer = layer;

    document.querySelectorAll('.layer-item').forEach(item => {
      item.classList.toggle('active', item.dataset.layer === layer);
    });

    document.getElementById('status-layer').textContent = layer.toUpperCase();

    // Notify scene
    this.scene.onLayerChanged(layer);
  }

  toggleLayerVisibility(layer) {
    this.layerVisibility[layer] = !this.layerVisibility[layer];

    const visBtn = document.querySelector(`.layer-visibility[data-layer="${layer}"]`);
    visBtn.classList.toggle('hidden', !this.layerVisibility[layer]);
    visBtn.textContent = this.layerVisibility[layer] ? 'O' : '-';

    // Notify scene
    this.scene.onLayerVisibilityChanged(layer, this.layerVisibility[layer]);
  }

  async loadMaps() {
    try {
      const response = await fetch('/api/editor/maps');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.maps = data.maps;
    } catch (error) {
      console.error('Failed to load maps:', error);
    }
  }

  showNewMapDialog() {
    const overlay = document.createElement('div');
    overlay.className = 'editor-modal-overlay';
    overlay.innerHTML = `
      <div class="editor-modal">
        <h2>CREATE NEW MAP</h2>
        <label class="editor-label">Map Name</label>
        <input type="text" class="editor-input" id="new-map-name" placeholder="level_01" value="level_01">

        <label class="editor-label">Width (tiles)</label>
        <input type="number" class="editor-input" id="new-map-width" value="25" min="10" max="100">

        <label class="editor-label">Height (tiles)</label>
        <input type="number" class="editor-input" id="new-map-height" value="19" min="10" max="100">

        <div class="editor-modal-buttons">
          <button class="editor-btn danger" id="btn-cancel-new">CANCEL</button>
          <button class="editor-btn" id="btn-confirm-new">CREATE</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    overlay.querySelector('#btn-cancel-new').addEventListener('click', () => overlay.remove());
    overlay.querySelector('#btn-confirm-new').addEventListener('click', () => {
      const name = document.getElementById('new-map-name').value || 'Untitled';
      const width = parseInt(document.getElementById('new-map-width').value) || 25;
      const height = parseInt(document.getElementById('new-map-height').value) || 19;
      this.createNewMap(name, width, height);
      overlay.remove();
    });
  }

  createNewMap(name, width, height) {
    // Create empty map data
    this.mapData = {
      id: null,
      name: name,
      version: 1,
      width: width,
      height: height,
      tile_size: 32,
      layers: {
        background: Array(height).fill(null).map(() => Array(width).fill(0)),
        collision: Array(height).fill(null).map(() => Array(width).fill(0)),
        decoration: Array(height).fill(null).map(() => Array(width).fill(0)),
      },
      entities: [],
      metadata: {
        music_track: 'Midnight-Reactor.mp3',
        ambience: '',
        mission_objectives: [],
      },
    };

    this.mapId = null;
    this.hasUnsavedChanges = false;
    this.history.clear();

    // Update status
    document.getElementById('status-map-name').textContent = name;
    document.getElementById('status-map-size').textContent = `${width}x${height}`;

    // Notify scene
    this.scene.loadMapData(this.mapData);
  }

  showLoadMapDialog() {
    const overlay = document.createElement('div');
    overlay.className = 'editor-modal-overlay';

    let mapsHtml = this.maps.length === 0
      ? '<p style="color: var(--text-dim)">No saved maps found.</p>'
      : this.maps.map(m => `
          <div class="level-item" data-map-id="${escapeHtml(m.id)}">
            <span>${escapeHtml(m.name)}</span>
            <span class="version">v${m.version} | ${m.width}x${m.height}</span>
          </div>
        `).join('');

    overlay.innerHTML = `
      <div class="editor-modal">
        <h2>LOAD MAP</h2>
        <div style="max-height: 300px; overflow-y: auto;">
          ${mapsHtml}
        </div>
        <div class="editor-modal-buttons">
          <button class="editor-btn danger" id="btn-cancel-load">CANCEL</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    overlay.querySelector('#btn-cancel-load').addEventListener('click', () => overlay.remove());
    overlay.querySelectorAll('.level-item').forEach(item => {
      item.addEventListener('click', () => {
        this.loadMap(item.dataset.mapId);
        overlay.remove();
      });
    });
  }

  async loadMap(mapId) {
    try {
      const response = await fetch(`/api/editor/maps/${mapId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.mapData = data.map;
      this.mapId = mapId;
      this.hasUnsavedChanges = false;
      this.history.clear();

      // Update status
      document.getElementById('status-map-name').textContent = this.mapData.name;
      document.getElementById('status-map-size').textContent = `${this.mapData.width}x${this.mapData.height}`;

      // Notify scene
      this.scene.loadMapData(this.mapData);
    } catch (error) {
      console.error('Failed to load map:', error);
      alert('Failed to load map');
    }
  }

  async saveMap() {
    if (!this.mapData) {
      this.showNewMapDialog();
      return;
    }

    try {
      const method = this.mapId ? 'PUT' : 'POST';
      const url = this.mapId
        ? `/api/editor/maps/${this.mapId}`
        : '/api/editor/maps';

      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.mapData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.mapId = data.id;
      this.mapData.version = data.version;
      this.hasUnsavedChanges = false;

      // Update status to remove unsaved indicator
      document.getElementById('status-map-name').textContent = this.mapData.name;

      // Refresh maps list
      await this.loadMaps();

      console.log('Map saved:', data.message);
    } catch (error) {
      console.error('Failed to save map:', error);
      alert('Failed to save map');
    }
  }

  toggleMetadataPanel() {
    const panel = document.getElementById('metadata-panel');
    panel.classList.toggle('visible');

    if (panel.classList.contains('visible') && this.mapData) {
      document.getElementById('meta-name').value = this.mapData.name || '';
      document.getElementById('meta-music').value = this.mapData.metadata?.music_track || '';
      document.getElementById('meta-objectives').value =
        (this.mapData.metadata?.mission_objectives || []).join('\n');
    }
  }

  applyMetadata() {
    if (!this.mapData) return;

    this.mapData.name = document.getElementById('meta-name').value || 'Untitled';
    this.mapData.metadata = this.mapData.metadata || {};
    this.mapData.metadata.music_track = document.getElementById('meta-music').value;
    this.mapData.metadata.mission_objectives =
      document.getElementById('meta-objectives').value.split('\n').filter(l => l.trim());

    document.getElementById('status-map-name').textContent = this.mapData.name;
    this.hasUnsavedChanges = true;

    document.getElementById('metadata-panel').classList.remove('visible');
  }

  testMap() {
    // Save to localStorage and open game in new tab
    if (this.mapData) {
      localStorage.setItem('testMap', JSON.stringify(this.mapData));
      window.open('/?testMap=true', '_blank');
    }
  }

  startAutoSave() {
    this.autoSaveTimer = setInterval(() => {
      if (this.hasUnsavedChanges) {
        this.autoSaveToLocalStorage();
      }
    }, 30000); // Every 30 seconds
  }

  autoSaveToLocalStorage() {
    if (this.mapData) {
      localStorage.setItem('editorAutoSave', JSON.stringify({
        mapData: this.mapData,
        mapId: this.mapId,
        timestamp: Date.now(),
      }));

      // Show indicator briefly
      const indicator = document.getElementById('autosave-status');
      indicator.style.display = 'block';
      setTimeout(() => {
        indicator.style.display = 'none';
      }, 2000);
    }
  }

  checkAutoSaveRecovery() {
    const saved = localStorage.getItem('editorAutoSave');
    if (saved) {
      const data = JSON.parse(saved);
      const age = Date.now() - data.timestamp;

      // If less than 1 hour old
      if (age < 3600000) {
        if (confirm('Unsaved changes found. Recover?')) {
          this.mapData = data.mapData;
          this.mapId = data.mapId;
          document.getElementById('status-map-name').textContent = this.mapData.name;
          document.getElementById('status-map-size').textContent =
            `${this.mapData.width}x${this.mapData.height}`;
          this.scene.loadMapData(this.mapData);
        }
      }

      localStorage.removeItem('editorAutoSave');
    }
  }

  updateCursorPosition(x, y) {
    document.getElementById('status-cursor').textContent = `${x}, ${y}`;
  }

  updateUndoRedoButtons(state) {
    document.getElementById('btn-undo').disabled = !state.canUndo;
    document.getElementById('btn-redo').disabled = !state.canRedo;
  }

  markUnsavedChanges() {
    this.hasUnsavedChanges = true;
    const nameEl = document.getElementById('status-map-name');
    if (!nameEl.textContent.endsWith('*')) {
      nameEl.textContent += '*';
    }
  }

  destroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
    }
    if (this.container) {
      this.container.remove();
    }
  }
}

export default EditorUI;
